import { useState, type CSSProperties, type ReactElement } from "react";
import {
  SPECIES,
  getItemDef,
  type GridState,
  type Inventory,
  type QuestGoal,
  type QuestState,
  type TraderDef,
} from "@rietz/shared";
import { uiActions } from "../actions.js";
import { GOOD, LINE_STRONG, RADIUS, SHADOW_MODAL, SURFACE_2 } from "../theme.js";
import { formatEuros } from "./format.js";
import { IconCheck, IconQuests } from "./icons.js";
import {
  Badge,
  Bar,
  Card,
  CardTitle,
  Divider,
  EmptyState,
  Eyebrow,
  ListRow,
  Note,
  Num,
  SplitPage,
  Title,
} from "./kit.js";
import { eligibleTurnIns, questBoard, type QuestRow, type TurnInCandidate } from "./questView.js";
import { TraderPortrait } from "./traderPortrait.js";

/**
 * The quest log: the trader screen's master/detail rhythm. Locked quests are
 * absent — `questBoard`'s rule.
 */
export interface QuestsTabProps {
  quests: readonly QuestState[];
  /**
   * The AUTHORITATIVE stash and kit, not the store's displayed copies — a
   * handover is charged server-side out of exactly these.
   */
  stash: GridState;
  kit: Inventory;
  /**
   * Whether the trade-in counter is open: true in the menu, FALSE for the
   * raid's copy of the log — no trader in the raid, and a dead button is worse
   * than none.
   */
  handover?: boolean;
}

export function QuestsTab(props: QuestsTabProps): ReactElement {
  const board = questBoard(props.quests);
  const [traderId, setTraderId] = useState(
    board.find((entry) => entry.quests.length > 0)?.trader.id ?? board[0]?.trader.id ?? "",
  );
  const selected = board.find((entry) => entry.trader.id === traderId) ?? board[0] ?? null;
  const allRows = board.flatMap((entry) => entry.quests);
  const completed = allRows.filter((row) => row.done).length;

  if (selected === null) {
    return (
      <Card style={{ width: "100%", maxWidth: 1000 }}>
        <EmptyState icon={<IconQuests />} title="No quests available." />
      </Card>
    );
  }

  const open = selected.quests.filter((row) => !row.done).length;

  return (
    <SplitPage
      side={240}
      maxWidth={1000}
      aside={
        <>
          <CardTitle aside={`${completed}/${allRows.length}`}>QUEST GIVERS</CardTitle>
          {board.map((entry) => {
            const done = entry.quests.filter((row) => row.done).length;
            return (
              <ListRow
                key={entry.trader.id}
                selected={entry.trader.id === selected.trader.id}
                onClick={() => setTraderId(entry.trader.id)}
                leading={<Portrait trader={entry.trader} size={40} />}
                title={entry.trader.name}
                subtitle={
                  entry.quests.length === 0
                    ? "Nothing to do right now"
                    : `${entry.quests.length - done} active`
                }
                trailing={`${done}/${entry.quests.length}`}
              />
            );
          })}
        </>
      }
    >
      <header style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <Portrait trader={selected.trader} size={64} />
        <div style={{ display: "flex", flexDirection: "column", gap: 5, minWidth: 0 }}>
          <Title size="lg">{selected.trader.name}</Title>
          <Note style={{ maxWidth: 620 }}>{selected.trader.blurb}</Note>
        </div>
        <span style={{ flex: 1 }} />
        <Badge tone="neutral">{open} open</Badge>
      </header>

      <Divider />

      {selected.quests.length === 0 ? (
        <EmptyState icon={<IconQuests />} title="Nothing to do right now." />
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(290px, 1fr))",
            gap: 12,
            alignItems: "start",
          }}
        >
          {selected.quests.map((row) => (
            <QuestCard
              key={row.def.id}
              row={row}
              stash={props.stash}
              kit={props.kit}
              handover={props.handover !== false}
            />
          ))}
        </div>
      )}
    </SplitPage>
  );
}

/** A trader's picture, rounded by `.mm-portrait` rather than by its own edge. */
function Portrait(props: { trader: TraderDef; size: number }): ReactElement {
  return (
    <span className="mm-portrait">
      <TraderPortrait trader={props.trader} size={props.size} />
    </span>
  );
}

/** A quest card is a card inside a card: raised a shade, never outlined twice. */
const QUEST_CARD: CSSProperties = { padding: "13px 14px", gap: 9, background: SURFACE_2 };

function QuestCard(props: {
  row: QuestRow;
  stash: GridState;
  kit: Inventory;
  handover: boolean;
}): ReactElement {
  const { def, progress, done } = props.row;
  const frac = def.goal.count > 0 ? progress / def.goal.count : 0;
  const [picking, setPicking] = useState(false);

  // The counter is offered on an OPEN fetch quest only: a finished one takes
  // nothing, a kill count cannot be carried in, and in a raid there is nobody
  // to hand to.
  const bring = def.goal.t === "bringItems" && !done && props.handover ? def.goal : null;
  const candidates = bring === null ? [] : eligibleTurnIns(bring.defId, props.stash, props.kit);
  const nothingToGive = bring !== null && candidates.length === 0 ? NOTHING_TO_GIVE : null;

  return (
    <Card style={QUEST_CARD}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span
          style={{ flex: 1, minWidth: 0, fontWeight: 600, fontSize: 14, overflowWrap: "anywhere" }}
        >
          {def.name}
        </span>
        <Badge tone={done ? "good" : "accent"}>{done ? "Complete" : "Active"}</Badge>
      </div>

      <Note style={{ minHeight: 46 }}>{def.description}</Note>

      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
          <Eyebrow>OBJECTIVE</Eyebrow>
          <Note>{goalText(def.goal)}</Note>
        </div>
        {/* Only a fetch quest carries an address (see `QuestDef.spawnAt`), and
            when it does, WHERE is the whole answer to the task — a street and a
            number the player reads off a signpost, because the house stands
            somewhere slightly different in every generated raid. */}
        {def.spawnAt === undefined ? null : (
          <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
            <Eyebrow>FOUND AT</Eyebrow>
            <span style={{ fontSize: 12 }}>
              {def.spawnAt.street} {def.spawnAt.housenumber}
            </span>
          </div>
        )}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span style={{ flex: 1, minWidth: 0 }}>
          <Bar fill01={frac} done={done} />
        </span>
        <Num tone={done ? "good" : "default"}>
          {progress} / {def.goal.count}
        </Num>
      </div>

      {/* The counter, right under the bar the handover moves. Greyed WITH a
          reason beside it, the way the hideout's buttons are: a grey button
          nobody can explain is a bug report. */}
      {bring === null ? null : (
        <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
          <button
            type="button"
            className="metabtn metabtn-sm"
            disabled={nothingToGive !== null}
            title={nothingToGive ?? HAND_OVER}
            onClick={() => {
              setPicking(true);
            }}
          >
            {HAND_OVER}
          </button>
          {nothingToGive === null ? null : <Note>{nothingToGive}</Note>}
        </div>
      )}

      <Divider />
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <Eyebrow>REWARD</Eyebrow>
        <Num tone="accent" style={{ whiteSpace: "normal" }}>
          {rewardText(props.row)}
        </Num>
      </div>

      {picking && bring !== null ? (
        <TurnInPicker
          questId={def.id}
          questName={def.name}
          defId={bring.defId}
          remaining={Math.max(0, bring.count - progress)}
          candidates={candidates}
          onClose={() => {
            setPicking(false);
          }}
        />
      ) : null}
    </Card>
  );
}

/** The label, in one place: it is also the button's `title` when nothing is
 *  wrong with it, and the two must not drift apart. */
const HAND_OVER = "Hand over…";

/** Why the button is shut, which is the only reason it ever is. */
const NOTHING_TO_GIVE = "None in stash or kit.";

/**
 * WHICH ones — the handover dialogue, a modal like the queue panel with a
 * `zIndex` above everything a tab paints. Nothing optimistic: confirming sends
 * `questTurnIn` and closes; the `profile` moves the card. The server walks the
 * list in this order and splits the last stack, so the one rule here is that a
 * WHOLE extra instance is never selected past the remainder — the last may
 * overshoot; that is the split.
 */
function TurnInPicker(props: {
  questId: string;
  questName: string;
  defId: string;
  /** Units the quest still wants: `count - progress`, never negative. */
  remaining: number;
  candidates: readonly TurnInCandidate[];
  onClose: () => void;
}): ReactElement {
  const [chosen, setChosen] = useState<ReadonlySet<string>>(() => new Set());

  const name = getItemDef(props.defId)?.name ?? props.defId;
  // Derived from the CANDIDATES rather than kept as a list of clicks, so the
  // order the server sees is the order on screen — see the note above.
  const picked = props.candidates.filter((c) => chosen.has(c.iid));
  const units = picked.reduce((n, c) => n + c.stack, 0);
  const full = units >= props.remaining;
  const takes = Math.min(units, props.remaining);

  const toggle = (iid: string): void => {
    setChosen((prev) => {
      const next = new Set(prev);
      if (!next.delete(iid)) next.add(iid);
      return next;
    });
  };

  return (
    <div
      className="mm-scrim"
      onClick={props.onClose}
      style={{
        // Above every layer a tab draws (tiles 3, panels 20, ghost 40, tooltip
        // 60) and below the queue modal (65) and the invite prompt (66).
        zIndex: 64,
      }}
    >
      <section
        onClick={(ev) => {
          // The backdrop closes; the panel is not the backdrop.
          ev.stopPropagation();
        }}
        style={{
          width: 340,
          maxHeight: "80%",
          padding: "16px 18px",
          boxSizing: "border-box",
          borderRadius: RADIUS,
          background: SURFACE_2,
          border: `1px solid ${LINE_STRONG}`,
          boxShadow: SHADOW_MODAL,
          display: "flex",
          flexDirection: "column",
          gap: 10,
          minHeight: 0,
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Title>Hand over · {name}</Title>
          <Note>{props.questName}</Note>
        </div>

        <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
          <Eyebrow>SELECTED</Eyebrow>
          <Num tone={full ? "good" : "default"}>
            {units} / {props.remaining}
          </Num>
          <Note>units</Note>
        </div>

        <div
          style={{
            minHeight: 0,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {props.candidates.map((c) => {
            const on = chosen.has(c.iid);
            // Enough is enough: with the remainder covered, the only click left
            // on an unselected row would be one whole instance too many.
            const locked = full && !on;
            return (
              <ListRow
                key={c.iid}
                selected={on}
                disabled={locked}
                onClick={() => {
                  toggle(c.iid);
                }}
                leading={
                  <span style={{ width: 14, display: "grid", placeItems: "center", color: GOOD }}>
                    {on ? <IconCheck size={13} /> : null}
                  </span>
                }
                title={
                  <>
                    {name}
                    {c.stack > 1 ? <Note> ×{c.stack}</Note> : null}
                  </>
                }
                trailing={c.source}
              />
            );
          })}
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button type="button" className="metabtn" onClick={props.onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="metabtn"
            disabled={units === 0}
            onClick={() => {
              uiActions().questTurnIn(
                props.questId,
                picked.map((c) => c.iid),
              );
              props.onClose();
            }}
          >
            Hand over {takes}
          </button>
        </div>
      </section>
    </div>
  );
}

function rewardText(row: QuestRow): string {
  const rewards = [formatEuros(row.def.rewardEuros)];
  for (const item of row.def.rewardItems ?? []) {
    const def = getItemDef(item.defId);
    rewards.push(`${item.count}× ${def?.name ?? item.defId}`);
  }
  return rewards.join(" + ");
}

/** The exact, non-flavour goal shown beside the progress counter. */
function goalText(goal: QuestGoal): string {
  switch (goal.t) {
    case "killScavs":
      return `Kill ${goal.count} poachers`;
    case "killPmcs":
      return `Kill ${goal.count} other mercenaries`;
    // THE ONE GOAL WITH A NAME IN IT: there is exactly one boss, and "Kill 1
    // bosses" would be a spoiler about a category.
    case "killBoss":
      return goal.count <= 1 ? "Kill the Hunter" : `Kill the Hunter ${goal.count} times`;
    case "extract":
      return goal.extractId === undefined
        ? `Extract alive ${goal.count} times`
        : `Extract via “${goal.extractId}” ${goal.count} times`;
    case "bringItems": {
      const def = getItemDef(goal.defId);
      return `Hand over ${goal.count}× ${def?.name ?? goal.defId}`;
    }
    // "Shoot", not "Kill": a hunt reads as a different task. The plural comes
    // off
    // the species (`namePlural`), and the singular is lower-cased mid-sentence.
    case "killAnimals":
      return `Shoot ${goal.count} ${
        goal.count === 1
          ? SPECIES[goal.species].name.toLowerCase()
          : SPECIES[goal.species].namePlural
      }`;
  }
}
